import { useCallback, useEffect, useState } from 'react';
import { CadastrosPeloLink } from '../componentes/CadastrosPeloLink.tsx';
import { FormularioCliente } from '../componentes/FormularioCliente.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { ErroDaApi, apiClientes, type Cliente } from '../lib/api.ts';
import { iniciais } from '../lib/iniciais.ts';
import { usarEhMaster } from '../lib/papel.ts';
import '../estilos/clientes.css';
import '../estilos/sistema.css';
import '../estilos/lista-de-clientes.css';

export function Clientes({
  aoAbrirCliente,
  aoExpirarSessao,
}: {
  aoAbrirCliente: (id: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [lista, setLista] = useState<Cliente[] | null>(null);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [gerandoLink, setGerandoLink] = useState(false);
  const ehMaster = usarEhMaster();

  const tratar = useCallback(
    (e: unknown) => {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof Error ? e.message : 'Algo não funcionou.');
    },
    [aoExpirarSessao],
  );

  const recarregar = useCallback(async () => {
    try {
      setErro(null);
      const r = await apiClientes.listar(busca.trim() || undefined);
      setLista(r.clientes);
    } catch (e) {
      tratar(e);
    }
  }, [busca, tratar]);

  useEffect(() => {
    // Espera a digitação parar antes de buscar: uma requisição por letra
    // digitada não ajuda ninguém.
    const t = setTimeout(() => void recarregar(), busca ? 300 : 0);
    return () => clearTimeout(t);
  }, [recarregar, busca]);

  return (
    /*
      ⭐ **Sistema visual 2026, parte 4 — a lista de Clientes, 14/09/2026.** A
      folha de identidade no alto e a lista numa folha só, com a busca no topo
      dela e o monograma de cada cliente (o mesmo da ficha). ⛔ **Mudou a
      aparência, não o que se clica:** a mesma busca, o mesmo "Novo cliente" e
      a linha inteira abrindo a ficha.
    */
    <div className="clientes-2026">
      {erro && <p className="erro">{erro}</p>}

      <section className="s-folha s-identidade clientes-identidade">
        <div className="s-monograma clientes-monograma" aria-hidden="true">
          <Icone nome="clientes" tamanho={28} />
        </div>
        <div className="clientes-identidade-texto">
          <span className="s-sobrelinha">Carteira</span>
          <h1 className="titulo">Clientes</h1>
          <p className="clientes-apoio">Quem contrata a KAIRÓS.</p>
        </div>
        {/* ⭐ O link de cadastro (16/09/2026, PRD §43.11) mora ao lado do Novo cliente, e é só do master. */}
        <div className="cad-botoes">
          {ehMaster && (
            <button type="button" className="botao-secundario s-botao" onClick={() => setGerandoLink(true)}>
              <Icone nome="elo" tamanho={15} />
              Link de cadastro
            </button>
          )}
          <button type="button" className="botao-primario s-botao" onClick={() => setCriando(true)}>
            <Icone nome="mais" tamanho={15} />
            Novo cliente
          </button>
        </div>
      </section>

      {ehMaster && (
        <CadastrosPeloLink
          gerando={gerandoLink}
          aoFecharGerar={() => setGerandoLink(false)}
          aoAbrirCliente={aoAbrirCliente}
          aoMudarCarteira={() => void recarregar()}
          aoExpirarSessao={aoExpirarSessao}
        />
      )}

      <section className="s-folha clientes-lista" aria-label="Lista de clientes">
        <div className="s-folha-topo clientes-lista-topo">
          <div className="clientes-busca">
            <label htmlFor="busca" className="so-leitor-de-tela">
              Buscar
            </label>
            <Icone nome="lupa" tamanho={16} />
            <input
              id="busca"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, nome fantasia ou documento"
            />
          </div>
          {lista !== null && lista.length > 0 && (
            <span className="s-micro clientes-conta">
              {lista.length} {lista.length === 1 ? 'cliente' : 'clientes'}
            </span>
          )}
        </div>

        {lista === null ? (
          <p className="vazio">Carregando…</p>
        ) : lista.length === 0 ? (
          <p className="vazio">
            {busca
              ? 'Nenhum cliente encontrado com esse termo.'
              : 'Nenhum cliente ainda. O primeiro entra pelo botão acima.'}
          </p>
        ) : (
          <div className="lista-clientes">
            {lista.map((c) => (
              <button
                key={c.id}
                type="button"
                className="cartao-cliente"
                onClick={() => aoAbrirCliente(c.id)}
              >
                <span className="s-avatar" aria-hidden="true">
                  {iniciais(c.nome)}
                </span>
                <div className="cartao-cliente-corpo">
                  <p className="cartao-cliente-nome">
                    {c.nome}
                    {c.nomeFantasia && <span className="fantasia"> · {c.nomeFantasia}</span>}
                  </p>
                  <p className="cartao-cliente-meta">
                    <span className="etiqueta etiqueta-tipo">
                      {c.tipo === 'pj' ? 'PJ' : 'PF'}
                    </span>
                    <span className="dado">{c.documentoFormatado}</span>
                    {c.origem && <span className="origem">indicado por {c.origem}</span>}
                  </p>
                </div>
                <span
                  className={
                    'cartao-cliente-casos' + (c.casos === 0 ? ' s-pilula s-pilula-quieta' : ' s-pilula')
                  }
                >
                  {c.casos === 0 ? 'sem casos' : `${c.casos} caso${c.casos === 1 ? '' : 's'}`}
                </span>
                <span className="cartao-cliente-seta" aria-hidden="true">
                  <Icone nome="avancar" tamanho={16} />
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {criando && (
        <FormularioCliente
          inicial={null}
          aoFechar={() => setCriando(false)}
          aoSalvar={async (dados) => {
            const novo = await apiClientes.criar(dados);
            setCriando(false);
            aoAbrirCliente(novo.id);
          }}
        />
      )}
    </div>
  );
}
